import {
  RtbCampaignWebService,
  RestfulCampaignWebService,
  CampaignAnalyticsRequest
} from 'ws/RtbCampaignWebService';
import {
  RtbCampaign,
  RtbCampaignBasic,
  RtbOptimize,
  RtbCampaignPlanType,
  AdType,
  RTBCAMPAIGN_DEFAULT_AGE_MAX,
  RTBCAMPAIGN_DEFAULT_AGE_MIN,
  CampaignAnalytics,
  legacyOptimizeMap
} from './RtbCampaign';
import _ from 'lodash';
import { SelectOptions } from 'components/commonType';
import LocalDateTimeUtil from 'utils/LocalDateTimeUtil';
import { L2ObjectOptimizationGoal } from 'core/l2Object/L2Object';
import i18n from 'i18n';
import { getLimitationContentLabel, toServerStructure } from 'utils/LimitationUtil';
import { ageMaxOptions, ageMinOptions, getRtbAgeGroupsByAgeRange } from 'core/limitation/l2ObjectTAOptions';
import { Limitation, LimitationData } from 'core/limitation/Limitation';
import { Partial } from 'react-spring';
import { DAILY_BUDGET_STATE } from 'containers/RtbCampaigns/RtbCampaignSetupFlow/RtbCampaignForm/RtbCampaignBasicFormModel';
import moment from 'moment';
export interface RtbCampaignManager {
  getCampaignsOfGroup (l1ObjectId: number | string): Promise<RtbCampaignBasic[]>;
  getCampaign (campaignId): Promise<RtbCampaign>;
  getNoCidCampaign (campaignId): Promise<RtbCampaign>;
  createCampaign (campaign: RtbCampaign, l1ObjectId?: number | string): Promise<number>;
  createPmpCampaign (campaign: RtbCampaign, l1ObjectId: number | string, pmpId: number, creative: any): Promise<number>;
  updateCampaign (campaign: RtbCampaign, l1ObjectId?: number | string): Promise<number>;
  splitCampaign (campaign: RtbCampaign, origCamaignId, l1ObjectId?: number | string): Promise<number>;
  updateCampaignState (campaignData: {
    l2ChannelId: (number | string)
  }[], state: 'activate' | 'deactivate'): Promise<void>;
  deleteCampaigns (campaignIds: Array<number>): Promise<void>;
  getCampaignName (campaignId: number): Promise<string | null>;
  getOrderNumber (campaignId: string | number): Promise<string>;
  prepareCreateCampaignPayload (campaign: RtbCampaign);
  getOptimizeDes (l2ObjectOptimizationGoal: L2ObjectOptimizationGoal): 'CPC' | 'CPM';
  getLimitationSummaryData (limitation: LimitationData): any;
  checkOptimizeSameAsPriceModel (priceModel: RtbCampaignPlanType, optimize: L2ObjectOptimizationGoal): boolean;
  getCampaignTypeDes (type: AdType): string;
  getRedirectInfo (campaignId: number | string): Promise<{
    orderNumber: string,
    l1ObjectId: string
  }>;
  getCampaignAnalytics (request: CampaignAnalyticsRequest, l1ObjectId: number | string): Promise<CampaignAnalytics>;
  getCampaignAnalyticsHint (campaignAnalytics: CampaignAnalytics | undefined, fieldName: string): string;
  getCampaignDailyBudgetState (
    budget: number,
    dailyTargetBudget: number | undefined,
    startDate: string,
    endDate: string
  ): DAILY_BUDGET_STATE;
  editCampaignBudget (l1ObjectId: number, campaignId: number, budget: number, dailyBudget: number | undefined);
  editCampaignDuration (l1ObjectId: number, campaignId: number, startDate: string, endDate: string);
}

export class DefaultRtbCampaignManager implements RtbCampaignManager {
  webService: RtbCampaignWebService;

  constructor (
    webService: RtbCampaignWebService = new RestfulCampaignWebService()
  ) {
    this.webService = webService;
  }

  async getCampaign (campaignId: number): Promise<RtbCampaign> {
    return this.webService.getCampaign(campaignId);
  }

  async getNoCidCampaign (campaignId: number): Promise<RtbCampaign> {
    const campaign = await this.getCampaign(campaignId);
    _.unset(campaign, 'basic.id');
    _.set(campaign, 'basic.name', campaign.basic.name + '_' + LocalDateTimeUtil.getYMDHMS());
    return campaign;
  }

  async getCampaignsOfGroup (l1ObjectId: number | string): Promise<RtbCampaignBasic[]> {
    return this.webService.getCampaignsOfGroup(l1ObjectId);
  }

  async createCampaign (campaign: RtbCampaign, l1ObjectId?: number | string): Promise<number> {
    const payload = this.prepareCreateCampaignPayload(campaign);
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    return this.webService.createCampaign(payload, l1ObjectId);
  }

  async createPmpCampaign (campaign: RtbCampaign, l1ObjectId: number | string, pmpId: number, creative: any): Promise<number> {
    const campaignPayload = this.prepareCreateCampaignPayload(campaign);
    _.set(campaignPayload, 'campaign.l1ObjectId', l1ObjectId);

    const dealIdTA = _.defaultTo(campaign.limitations.other, []).find(limitation => limitation.type === 'dealId');
    const payload = {
      ...campaignPayload,
      campaign: _.omit({
        ...campaignPayload.campaign,
        dealIds: dealIdTA ? (dealIdTA.value as SelectOptions[]).map(limit => limit.value) : [],
        pmpId
      }, ['ageMin', 'ageMax']),
      limitations: [],
      creatives: [creative]
    };
    return this.webService.createPmpCampaign(payload, l1ObjectId);
  }

  async updateCampaign (campaign: RtbCampaign, l1ObjectId?: number | string): Promise<number> {
    const payload = this.wrapCampaignForServer(_.cloneDeep(campaign));
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    return this.webService.updateCampaign(payload, l1ObjectId);
  }

  async splitCampaign (campaign: RtbCampaign, origCamaignId, l1ObjectId?: number | string): Promise<number> {
    const payload = this.prepareCreateCampaignPayload(campaign);
    _.set(payload, 'campaign.l1ObjectId', l1ObjectId);
    return this.webService.splitCampaign(payload, origCamaignId, l1ObjectId);
  }

  async updateCampaignState (campaignData: {
    l2ChannelId: (number | string)
  }[], state: 'activate' | 'deactivate'): Promise<void> {
    await this.webService.updateCampaignState(campaignData, state);
  }

  async deleteCampaigns (campaignIds: Array<number>): Promise<void> {
    return this.webService.deleteCampaigns(campaignIds);
  }

  async getCampaignName (campaignId: number): Promise<string | null> {
    return this.webService.getCampaignName(campaignId);
  }

  async getOrderNumber (campaignId: string | number): Promise<string> {
    return this.webService.getOrderNumber(campaignId);
  }

  async getRedirectInfo (campaignId: number | string): Promise<{
    orderNumber: string,
    l1ObjectId: string
  }> {
    return this.webService.getRedirectInfo(campaignId);
  }

  async getCampaignAnalytics (request: CampaignAnalyticsRequest, l1ObjectId: number | string): Promise<CampaignAnalytics> {
    return this.webService.getCampaignAnalytics(request, l1ObjectId);
  }

  getCampaignAnalyticsHint (campaignAnalytics: CampaignAnalytics | undefined, fieldName: string): string {
    const neglectedFields = ['brandAwarenessGrowth', 'trafficGrowth', 'keywordSearchVolume'];
    const analytics = _.get(campaignAnalytics, fieldName);
    if (!campaignAnalytics || !analytics || neglectedFields.includes(fieldName)) {
      return '';
    }
    let value: string | number | null;
    switch (fieldName) {
      case 'lifetimeBudgetRank':
      case 'dailyBudgetRank':
        const percentage = Math.round(analytics * 100);
        value = (100 - percentage) > 20 ? `${100 - percentage}%` : null;
        break;
      default:
        value = analytics;
        break;
    }
    if (!value) {
      return '';
    }
    return i18n.t<string>(`campaignInfo.analytics.${fieldName}`, { value });
  }

  getNativeOptimizeValue (l2ObjectOptimizationGoal: L2ObjectOptimizationGoal) {
    const nativeOptimizeMap = {
      [L2ObjectOptimizationGoal.IMPRESSIONS]: RtbOptimize.IMPRESSIONS,
      [L2ObjectOptimizationGoal.CLICKS]: RtbOptimize.CLICKS
    };
    return nativeOptimizeMap[l2ObjectOptimizationGoal];
  }

  getOptimizeDes (l2ObjectOptimizationGoal: L2ObjectOptimizationGoal) {
    return legacyOptimizeMap[l2ObjectOptimizationGoal] ? legacyOptimizeMap[l2ObjectOptimizationGoal] : l2ObjectOptimizationGoal;
  }

  wrapCampaignForServer = (campaign: RtbCampaign) => {
    const includeTA = campaign.limitations.include;
    const preferredTA = campaign.limitations.preferred;
    const ageOptValue = includeTA && includeTA.find(limitation => limitation.type === 'age_min') ? 'include' : 'preferred';
    const ageMinLimitation = ageOptValue === 'include'
     ? includeTA.find(limitation => limitation.type === 'age_min') : preferredTA
     ? preferredTA.find(limitation => limitation.type === 'age_min') : undefined;
    const ageMin = ageMinLimitation ? ageMinLimitation.value : RTBCAMPAIGN_DEFAULT_AGE_MIN;
    const ageMaxLimitation = ageOptValue === 'include'
     ? includeTA.find(limitation => limitation.type === 'age_max') : preferredTA
     ? preferredTA.find(limitation => limitation.type === 'age_max') : undefined;
    const ageMax = ageMaxLimitation ? ageMaxLimitation.value : RTBCAMPAIGN_DEFAULT_AGE_MAX;
    const isRs = [RtbCampaignPlanType.RS_CPC, RtbCampaignPlanType.RS_CPM, RtbCampaignPlanType.RS].includes(campaign.basic.priceModel);
    const recommendedKeywordsLimitation = includeTA && includeTA.find(limitation => limitation.type === 'recommendedKeywords');
    let result: any = {
      campaign: {
        ..._.omit(campaign.basic, ['id', 'tags']),
        priceModel: isRs ? RtbCampaignPlanType.RS : campaign.basic.priceModel,
        tags: campaign.basic.tags.join(','),
        budget: campaign.basic.budget,
        campaignId: campaign.basic.id,
        ageMin: ageMin,
        ageMax: ageMax,
        optimize: this.getNativeOptimizeValue(campaign.basic.optimize)
      },
      limitations: ageOptValue === 'include' ? toServerStructure(campaign.limitations) : toServerStructure({
        ...campaign.limitations,
        preferred: [
          ..._.filter(preferredTA, (limitation: Limitation) => !['age_min', 'age_max'].includes(limitation.type)),
          {
            op: 'Preferred',
            type: 'age',
            value: getRtbAgeGroupsByAgeRange(ageMin, ageMax)
          }
        ]
      }),
      creatives: campaign.products ? campaign.products.map(product => ({
        typeProperties: {
          product: JSON.stringify(product)
        }
      })) : undefined,
      recommenderVersion: recommendedKeywordsLimitation ? _.get(recommendedKeywordsLimitation.value, '[0].extra.version') : undefined
    };
    return result;
  }

  prepareCreateCampaignPayload (campaign: RtbCampaign) {
    const prepareCreate = _.flow([_.cloneDeep, this.wrapCampaignForServer]);
    return prepareCreate(campaign);
  }

  getAgeLimitationSummary = (limitations: Limitation[]) => {
    const ageMinLimitation = limitations.find(limitation => limitation.type === 'age_min');
    const ageMaxLimitation = limitations.find(limitation => limitation.type === 'age_max');
    if (ageMinLimitation && ageMaxLimitation) {
      const ageMinOption = ageMinOptions.find(option => option.value === _.get(ageMinLimitation, 'value'));
      const ageMaxOption = ageMaxOptions.find(option => option.value === _.get(ageMaxLimitation, 'value'));
      const ageGroupsHintGetter = getRtbAgeGroupsByAgeRange;
      const ageGroupsHint = ageGroupsHintGetter(_.get(ageMinOption, 'value', 0), _.get(ageMaxOption, 'value', 0))
                              .map(group => group.label)
                              .join(',');
      return {
        label: i18n.t<string>('limitation.labels.age'),
        value: `${_.get(ageMinOption, 'label', 0)} ~ ${_.get(ageMaxOption, 'label', 0)}`,
        hint: i18n.t<string>('limitation.hints.ageGroups', { groups: ageGroupsHint })
      };
    }
    return undefined;
  }

  getSearchKeywordsLimitationSummary = (limitationData: Limitation[]) => {
    // merge 'searchKeywords' and 'recommendedKeywords' into 'searchKeywords'
    const searchKeywordsLimitation = limitationData.find(limitation => limitation.type === 'searchKeywords');
    const recommendedKeywordsLimitation = limitationData.find(limitation => limitation.type === 'recommendedKeywords');
    const searchKeywords = searchKeywordsLimitation ? searchKeywordsLimitation.value as SelectOptions[] : [];
    const recommendedKeywords = recommendedKeywordsLimitation ? recommendedKeywordsLimitation.value as SelectOptions[] : [];
    const allKeywords = _.uniqBy([...searchKeywords, ...recommendedKeywords], 'value');

    if (allKeywords.length !== 0) {
      return {
        label: i18n.t<string>('limitation.labels.searchKeywords'),
        value: allKeywords.map(keyword => keyword.value).join(', ')
      };
    }
    return undefined;
  }

  getLimitationContent = (limitationData: Limitation[]) => {
    if (!limitationData) {
      return [];
    }
    let limitationSummary: Partial<SelectOptions | { hint?: string }>[] = [];
    const normalLimitationSummary = _.compact(limitationData.map((data) => {
      const ignoreType = [
        'age',
        'age_min',
        'age_max',
        'searchKeywords',
        'recommendedKeywords'
      ];
      if (ignoreType.includes(data.type)) {
        return undefined;
      }

      if (!data.value) {
        return undefined;
      }

      if (Array.isArray(data.value)) {
        if (_.defaultTo(data.value, []).length === 0) {
          return undefined;
        }

        return {
          label: i18n.t<string>(`limitation.labels.${data.type}`),
          value: data.value.map((value) => getLimitationContentLabel(data.type, value)).join(', ')
        };
      }

      return {
        label: i18n.t<string>(`limitation.labels.${data.type}`),
        value: data.value
      };
    }));
    limitationSummary = [...limitationSummary, ...normalLimitationSummary];

    // const ageLimitationSummary = this.getAgeLimitationSummary(limitationData);
    // if (ageLimitationSummary) {
    //   limitationSummary.unshift(ageLimitationSummary);
    // }
    const searchKeywordsLimitationSummary = this.getSearchKeywordsLimitationSummary(limitationData);
    if (searchKeywordsLimitationSummary) {
      limitationSummary.push(searchKeywordsLimitationSummary);
    }
    return limitationSummary;
  }

  getLimitationSummaryData (limitationData: LimitationData) {
    return _.omitBy({
      include: this.getLimitationContent(limitationData.include),
      preferred: this.getLimitationContent(limitationData.preferred),
      nonPreferred: this.getLimitationContent(limitationData.nonPreferred),
      exclude: this.getLimitationContent(limitationData.exclude),
      other: limitationData.other ? this.getLimitationContent(limitationData.other) : undefined
    }, _.isEmpty);
  }

  checkOptimizeSameAsPriceModel (priceModel: RtbCampaignPlanType, optimize: L2ObjectOptimizationGoal): boolean {
    return (priceModel === RtbCampaignPlanType.FCPC && optimize === L2ObjectOptimizationGoal.CLICKS) ||
      (priceModel === RtbCampaignPlanType.FCPM && optimize === L2ObjectOptimizationGoal.IMPRESSIONS);
  }

  getCampaignTypeDes = (adType) => {
    if (adType in AdType) {
      return i18n.t<string>(`campaignList.labels.adType${_.upperFirst(_.camelCase(adType))}`);
    }
    return i18n.t<string>('campaignList.labels.adTypeEmpty');
  }

  getCampaignDailyBudgetState = (
    totalBudget: number,
    dailyBudget: number | undefined,
    startDate: string,
    endDate: string
  ): DAILY_BUDGET_STATE => {
    if (!dailyBudget || dailyBudget < 1 || dailyBudget > totalBudget) {
      return DAILY_BUDGET_STATE.DEFAULT;
    }

    const format = 'YYYY-MM-DD';
    const totalDay = moment(endDate, format)
      .add(1, 'days')
      .diff(moment(startDate, format), 'days');

    const lastDay = totalBudget / dailyBudget;
    if (lastDay === totalDay) {
      return DAILY_BUDGET_STATE.MEET_BUDGET;
    }
    if (lastDay < totalDay) {
      return DAILY_BUDGET_STATE.OVER_BUDGET;
    }
    return DAILY_BUDGET_STATE.UNDER_BUDGET;
  }

  editCampaignBudget (l1ObjectId: number, campaignId: number, budget: number, dailyBudget: number | undefined) {
    return this.webService.editCampaignBudget(l1ObjectId, campaignId, budget, dailyBudget);
  }

  editCampaignDuration (l1ObjectId: number, campaignId: number, startDate: string, endDate: string) {
    return this.webService.editCampaignDuration(l1ObjectId, campaignId, startDate, endDate);
  }
}
